import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import PrismLogo from "@/components/PrismLogo";

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const { refresh } = useAuth();
  const [status, setStatus] = useState(token ? "loading" : "notoken");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setStatus("success");
        setMsg(data?.message || "Email verified");
        await refresh();
      } catch (ex) {
        setStatus("error");
        setMsg(formatApiError(ex));
      }
    })();
  }, [token, refresh]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6" data-testid="verify-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-8">
          <PrismLogo size={36} />
          <span className="font-display text-xl font-extrabold text-slate-900">Prismiq</span>
        </Link>

        <div className="qs-card p-8 text-center">
          {status === "loading" && (
            <>
              <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto" />
              <h1 className="font-display text-2xl font-black text-slate-900 mt-4">Verifying…</h1>
            </>
          )}
          {status === "success" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-2xl bg-emerald-50 text-emerald-600 grid place-items-center">
                <CheckCircle2 className="w-7 h-7" />
              </div>
              <h1 className="font-display text-2xl font-black text-slate-900 mt-4" data-testid="verify-success">Email verified 🎉</h1>
              <p className="mt-2 text-slate-600 text-sm">{msg} — you can now use every feature of your account.</p>
              <Link to="/" className="btn-primary mt-6 text-sm inline-flex" data-testid="verify-continue">Continue</Link>
            </>
          )}
          {status === "error" && (
            <>
              <div className="w-14 h-14 mx-auto rounded-2xl bg-rose-50 text-rose-600 grid place-items-center">
                <XCircle className="w-7 h-7" />
              </div>
              <h1 className="font-display text-2xl font-black text-slate-900 mt-4" data-testid="verify-error">Verification failed</h1>
              <p className="mt-2 text-slate-600 text-sm">{msg}</p>
              <Link to="/" className="btn-ghost mt-6 text-sm inline-flex">Back home</Link>
            </>
          )}
          {status === "notoken" && (
            <>
              <h1 className="font-display text-2xl font-black text-slate-900">Missing token</h1>
              <p className="mt-2 text-slate-600 text-sm">Open the link from your verification email.</p>
              <Link to="/" className="btn-ghost mt-6 text-sm inline-flex">Back home</Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
